/**
 * Authorization API types
 */

import { z } from 'zod';
import { AuthorizableAction, ResourceType, Status } from '@pillar-portal/database/dist/frontend';
import type { ResourceDesignator } from './permission.types';

export const AUTHORIZATION_MAX_PAGE_LIMIT: number = 1000;

/**
 * Cursor-based pagination metadata.
 *
 * We use numeric cursors for ID-ordered endpoints; the client serializes them into
 * query params as strings.
 */
export const CursorPaginationSchema = z.object({
    has_more: z.boolean(),
    next_cursor: z.number().int().positive().optional(),
});

export type CursorPagination = z.infer<typeof CursorPaginationSchema>;

/**
 * Cursor-based pagination metadata for opaque (non-ID) cursors.
 *
 * Use this for endpoints whose pagination cursor must not be a database pkey.
 */
export const OpaqueCursorPaginationSchema: z.ZodType<{
    has_more: boolean;
    next_cursor?: string;
}> = z.object({
    has_more: z.boolean(),
    next_cursor: z.string().trim().min(1).optional(),
});

export type OpaqueCursorPagination = z.infer<typeof OpaqueCursorPaginationSchema>;

/**
 * Response for can_create_user endpoint
 */
export const CanCreateUserResponseSchema = z.object({
    can_create: z.boolean(),
});

export type CanCreateUserResponse = z.infer<typeof CanCreateUserResponseSchema>;

/**
 * Response for can_modify_user endpoint
 */
export const CanModifyUserResponseSchema = z.object({
    can_modify: z.boolean(),
});

export type CanModifyUserResponse = z.infer<typeof CanModifyUserResponseSchema>;

/**
 * Response for can_delete_user endpoint
 */
export const CanDeleteUserResponseSchema = z.object({
    can_delete: z.boolean(),
});

export type CanDeleteUserResponse = z.infer<typeof CanDeleteUserResponseSchema>;

export const CanImpersonateRoleAssignmentResponseSchema = z.object({
    can_impersonate: z.boolean(),
});

export type CanImpersonateRoleAssignmentResponse = z.infer<
    typeof CanImpersonateRoleAssignmentResponseSchema
>;

/**
 * Response for has_access_to_organization endpoint
 */
export const HasAccessToOrganizationResponseSchema = z.object({
    has_access: z.boolean(),
});

export type HasAccessToOrganizationResponse = z.infer<typeof HasAccessToOrganizationResponseSchema>;

/**
 * Response for has_access_to_user endpoint
 */
export const HasAccessToUserResponseSchema = z.object({
    has_access: z.boolean(),
});

export type HasAccessToUserResponse = z.infer<typeof HasAccessToUserResponseSchema>;

/**
 * Response for accessible_organization_ids endpoint
 */
export const AccessibleOrganizationIdsResponseSchema = z.object({
    organization_uuids: z.array(z.string().uuid()),
    pagination: OpaqueCursorPaginationSchema,
});

export type AccessibleOrganizationIdsResponse = z.infer<
    typeof AccessibleOrganizationIdsResponseSchema
>;

/**
 * Response for accessible_user_ids endpoint
 */
export const AccessibleUserIdsResponseSchema = z.object({
    user_uuids: z.array(z.string().uuid()),
    pagination: OpaqueCursorPaginationSchema,
});

export type AccessibleUserIdsResponse = z.infer<typeof AccessibleUserIdsResponseSchema>;

/**
 * Response for accessible_organizations endpoint
 */
export interface AccessibleOrganizationSummary {
    organization_uuid: string;
    parent_organization_uuid: string | null;
    name: string;
    is_iso: boolean;
    is_merchant: boolean;
    is_customer: boolean;
    status: Status;
}

export const AccessibleOrganizationSummarySchema: z.ZodType<AccessibleOrganizationSummary> =
    z.object({
        organization_uuid: z.string().uuid(),
        parent_organization_uuid: z.string().uuid().nullable(),
        name: z.string(),
        is_iso: z.boolean(),
        is_merchant: z.boolean(),
        is_customer: z.boolean(),
        status: z.enum(Status),
    });

export const AccessibleOrganizationsResponseSchema: z.ZodType<{
    organizations: AccessibleOrganizationSummary[];
    pagination: OpaqueCursorPagination;
}> = z.object({
    organizations: z.array(AccessibleOrganizationSummarySchema),
    pagination: OpaqueCursorPaginationSchema,
});

export type AccessibleOrganizationsResponse = z.infer<typeof AccessibleOrganizationsResponseSchema>;

/**
 * Response for accessible_users endpoint
 */
export interface AccessibleUserSummary {
    user_uuid: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
    emails: string[];
    totp_enabled: boolean;
    status: string;
}

export const AccessibleUserSummarySchema: z.ZodType<AccessibleUserSummary> = z.object({
    user_uuid: z.string().uuid(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string().min(1),
    emails: z.array(z.string().min(1)),
    totp_enabled: z.boolean(),
    status: z.string().min(1),
});

export const AccessibleUsersResponseSchema: z.ZodType<{
    users: AccessibleUserSummary[];
    pagination: OpaqueCursorPagination;
}> = z.object({
    users: z.array(AccessibleUserSummarySchema),
    pagination: OpaqueCursorPaginationSchema,
});

export type AccessibleUsersResponse = z.infer<typeof AccessibleUsersResponseSchema>;

/**
 * Response for is_authorized endpoint
 */
export const IsAuthorizedResponseSchema = z.object({
    is_authorized: z.boolean(),
});

export type IsAuthorizedResponse = z.infer<typeof IsAuthorizedResponseSchema>;

/**
 * Request parameters for is_authorized endpoint
 */
export interface IsAuthorizedRequest {
    action: AuthorizableAction;
    resource_type: ResourceType;
    target_resource: ResourceDesignator;
}

export interface AccessibleOrganizationIdsRequest {
    limit?: number;
    cursor?: string;
}

export interface AccessibleUserIdsRequest {
    limit?: number;
    cursor?: number;
    organization_id?: number;
}

export interface AccessibleUserIdsRequestNoPkey {
    limit?: number;
    cursor?: string;
    organization_uuid?: string;
}
